import type { FormEvent, ReactNode, JSX } from 'react'

import { clsx } from 'clsx'

import {
  Flex,
  IconButton,
  SendHorizontalIcon,
  Text,
} from '@buffer-mono/popcorn'

import {
  ReplyFormSurfaceContext,
  useReplyFormSurface,
} from './ReplyFormContext'
import type { ReplyFormSurface } from './ReplyFormContext'

import styles from './ReplyForm.module.css'

const ROOT_SURFACE_CLASS: Record<ReplyFormSurface, string> = {
  subtle: styles.rootSubtle,
  card: styles.rootCard,
}

const FOOTER_SURFACE_CLASS: Record<ReplyFormSurface, string> = {
  subtle: styles.footerSubtle,
  card: styles.footerCard,
}

type ReplyFormRootProps = {
  surface: ReplyFormSurface
  /** Accessible name of the form, e.g. "Reply to comment from @user". */
  ariaLabel: string
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  className?: string
  /** The form's rows top to bottom: anything above the field, the field, the footer. */
  children: ReactNode
}

/**
 * The reply `<form>`. It stacks its children in order, so a row placed before
 * the field renders above it inside the form, and it tells the field and the
 * footer which surface to draw.
 */
function ReplyFormRoot({
  surface,
  ariaLabel,
  onSubmit,
  className,
  children,
}: ReplyFormRootProps): JSX.Element {
  return (
    <ReplyFormSurfaceContext.Provider value={surface}>
      <form
        aria-label={ariaLabel}
        onSubmit={onSubmit}
        className={clsx(ROOT_SURFACE_CLASS[surface], className)}
      >
        <Flex direction="column" fullWidth>
          {children}
        </Flex>
      </form>
    </ReplyFormSurfaceContext.Provider>
  )
}

/**
 * The sunken strip above the form, for whatever the surface says before the
 * reply: "Replying to" with suggestions, or how long is left to reply. The
 * strip is the same everywhere; only its content changes.
 *
 * Place it directly before `ReplyForm.Root` so the form's box tucks under
 * its bottom edge. Layouts that split the two (the comment avatar grid) set
 * that tuck themselves.
 */
function ReplyFormHeader({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Flex align="center" className={styles.header}>
      {children}
    </Flex>
  )
}

/** The row under the field: tools at the start, submit at the end. */
function ReplyFormFooter({ children }: { children: ReactNode }): JSX.Element {
  const surface = useReplyFormSurface()

  return (
    <Flex
      gap="sm"
      align="center"
      justify="between"
      wrap="wrap"
      className={clsx(styles.footer, FOOTER_SURFACE_CLASS[surface])}
    >
      {children}
    </Flex>
  )
}

/** The footer's leading group of icon buttons (emoji, saved replies, …). */
function ReplyFormFooterStart({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  return (
    <Flex direction="row" gap="2xs" align="center" wrap="wrap">
      {children}
    </Flex>
  )
}

/** The footer's trailing group: the character count and the submit button. */
function ReplyFormFooterEnd({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  return (
    <Flex direction="row" gap="xs" align="center" justify="end" wrap="wrap">
      {children}
    </Flex>
  )
}

type ReplyFormCharCountProps = {
  /** Characters used, counted the way the network counts them. */
  count: number
  limit: number
}

function ReplyFormCharCount({
  count,
  limit,
}: ReplyFormCharCountProps): JSX.Element {
  return (
    <Text size="sm" className={styles.charCount}>
      {count}/{limit}
    </Text>
  )
}

type ReplyFormSubmitProps = {
  /** Accessible name of the send icon button. */
  label: string
  disabled: boolean
  tooltip?: ReactNode
}

function ReplyFormSubmit({
  label,
  disabled,
  tooltip,
}: ReplyFormSubmitProps): JSX.Element {
  return (
    <IconButton
      type="submit"
      label={label}
      tooltip={tooltip}
      variant="primary"
      size="medium"
      className={styles.submit}
      disabled={disabled}
    >
      <SendHorizontalIcon />
    </IconButton>
  )
}

export {
  ReplyFormHeader,
  ReplyFormRoot,
  ReplyFormFooter,
  ReplyFormFooterStart,
  ReplyFormFooterEnd,
  ReplyFormCharCount,
  ReplyFormSubmit,
}
